/**
 * Guided agentic engagement — session model for the AI wizard.
 * Product: steps + artifacts (not chat-first). See docs/agnostic-architecture/PRODUCT_VISION.md
 */

import type { AgnosticBlueprint } from './agnosticBlueprint';
import type { LocalizedText } from './agnosticBlueprint';

export const AGENTIC_ENGAGEMENT_SCHEMA_VERSION = '1.0.0';

export type AgenticStepId =
  | 'discovery'
  | 'requirements'
  | 'architecture'
  | 'risks'
  | 'estimate'
  | 'team'
  | 'plan'
  | 'summary';

/** Effective access tier used for UI gating (may be overridden in DEV). */
export type AgenticAccessTier = 'guest' | 'free' | 'pro' | 'enterprise';

export type AgenticStepAccess = 'open' | 'login_required' | 'ai_quota' | 'premium' | 'full';

export interface AgenticAccessState {
  tier: AgenticAccessTier;
  isAuthenticated: boolean;
  plan: string;
  queriesLeft: number;
  /** Can call generateAiContent (auth + queriesLeft > 0). */
  canUseAi: boolean;
  /** Can see Risks / Estimate / Team / Plan details. */
  canViewPremiumArtifacts: boolean;
  /** Wave M2 — this engagement has a one-time report unlock (plan stays Free). */
  packPassUnlocked?: boolean;
  /** DEV-only override active. */
  isDemoOverride: boolean;
  /** Optional Faza 8 entitlements snapshot (credits + saved assessments). */
  entitlements?: {
    /** Billing plan from GET /entitlements (API SoT; may heal stale FE profile). */
    plan?: string;
    aiCreditLimit: number;
    engagementsActive: number | null;
    engagementsLimit: number;
    retentionDays: number | null;
    seatsUsed: number;
    seatsLimit: number;
  };
}

export interface AgenticOption {
  id: string;
  label: LocalizedText;
  description?: LocalizedText;
  /**
   * Within allowMulti: at most one option from the same exclusiveGroup may be selected
   * (e.g. cloud vendors behave like radio inside multi-select).
   */
  exclusiveGroup?: string;
  /** Optional group for tiered UX (e.g. product_type categories). */
  groupId?: string;
}

/** Ordered category labels for questions that use option.groupId. */
export interface AgenticOptionGroup {
  id: string;
  label: LocalizedText;
  /** Short hint under the category tile. */
  description?: LocalizedText;
}

export interface AgenticQuestion {
  id: string;
  stepId: AgenticStepId;
  prompt: LocalizedText;
  help?: LocalizedText;
  options: AgenticOption[];
  /** When set, GuidedQuestionCard uses category → options flow. */
  optionGroups?: AgenticOptionGroup[];
  /** Allow free-text note alongside option. */
  allowNote?: boolean;
  /** Placeholder when allowNote — must explain what the engine does with free text. */
  notePlaceholder?: LocalizedText;
  /** Multi-select — answer stored as comma-separated option ids. */
  allowMulti?: boolean;
  /**
   * `textarea` = paste brief / free text as the answer (stored in notes[id],
   * answers[id] = 'pasted' when confirmed). Default = option cards.
   */
  inputKind?: 'options' | 'textarea';
  /** Min characters for textarea confirm (default 40). */
  minNoteLength?: number;
  /** Cap for allowMulti (default: unlimited except exclusive "none"). */
  maxSelections?: number;
}

export interface RequirementsPack {
  productSummary: LocalizedText;
  domain: string;
  scale: string;
  constraints: string[];
  nfrPriorities: string[];
  capabilities?: string[];
  /** BA-light outcome framing from intake (optional). */
  businessOutcome?: {
    outcomeId: string;
    successSignalId: string | null;
    headline: LocalizedText;
    detail: LocalizedText;
  };
  unknowns: string[];
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
}

export type RiskItemSource = 'pack' | 'catalog_theme';

export interface RiskItem {
  id: string;
  title: LocalizedText;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  description: LocalizedText;
  mitigation: LocalizedText;
  /** Observable outcome — must differ from mitigation (gap-close Target state). */
  targetState?: LocalizedText;
  /** pack = delivery register; catalog_theme = promoted L1 riskThemes. */
  source?: RiskItemSource;
  /** Integration decision card that closes or supports this risk. */
  mitigationCardId?: string | null;
  /** Intake question to clarify (mutates guided answers). */
  clarifyQuestionId?: string | null;
  clarifyStepId?: 'discovery' | 'requirements' | null;
  /** In-product jump when no mutating action applies. */
  jumpHref?: string | null;
  jumpLabel?: LocalizedText | null;
  /**
   * Wave C — mutating close join derived from theme keywords (overlay / card / compose).
   * Generic rule — not a hard-coded theme title list.
   */
  closeJoin?: {
    kind: 'apply_overlay' | 'accept_decision_card' | 'compose_related_pattern';
    overlayId?: string | null;
    cardId?: string | null;
    patternId?: string | null;
    label: LocalizedText;
  } | null;
  /** Related L1 pattern to compose onto the diagram. */
  relatedPatternId?: string | null;
}

export interface RiskRegister {
  items: RiskItem[];
  summary: LocalizedText;
  /** Free / Guest locked stub — PremiumLock treats as upgrade-required. */
  requiresUpgrade?: boolean;
}

/** Rough order of magnitude — directional only, not a commercial quote. */
export type EstimateAccuracyClass = 'ROM';

/** Where effort weeks came from (diagram vs intake heuristics). */
export type EstimateEffortSource = 'intake' | 'topology' | 'hybrid';

export interface EstimatePack {
  effortWeeks: LocalizedText;
  teamSize: LocalizedText;
  costRange: LocalizedText;
  assumptions: LocalizedText[];
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
  requiresUpgrade?: boolean;
  /** Always ROM in Wave E0 — board honesty. */
  accuracyClass?: EstimateAccuracyClass;
  /** intake = scale/capabilities; hybrid/topology = L2 diagram drove the band. */
  effortSource?: EstimateEffortSource;
}

export interface TeamRoleSuggestion {
  role: LocalizedText;
  /** L3 hr-matrix catalog id (ADR-008 Team federation). */
  roleId?: string;
  count: number;
  seniority: string;
  rationale: LocalizedText;
}

export interface TeamPack {
  roles: TeamRoleSuggestion[];
  summary: LocalizedText;
  requiresUpgrade?: boolean;
}

/** Skills matrix row + JD stub from TeamPack + hr-matrix (Sprint 16). */
export interface TeamHrSkillsRow {
  roleId: string;
  roleName: LocalizedText;
  count: number;
  seniority: string;
  hiringDifficulty?: string | null;
  skillIds: string[];
}

export interface TeamHrJdStub {
  roleId: string;
  title: LocalizedText;
  headcount: number;
  seniority: string;
  opening: LocalizedText;
  responsibilities: LocalizedText[];
  competencies: LocalizedText[];
  packRationale: LocalizedText;
}

export interface TeamHrExport {
  schemaVersion: '1.0.0';
  updatedAt: string;
  disclaimer: LocalizedText;
  skillLabels: Array<{ id: string; label: LocalizedText }>;
  skillsMatrix: TeamHrSkillsRow[];
  jdStubs: TeamHrJdStub[];
}

export type HiringWaveId = 'foundation' | 'build' | 'harden';

export interface HiringTimelineItem {
  id: string;
  roleId: string;
  roleName: LocalizedText;
  count: number;
  seniority: string;
  wave: HiringWaveId;
  phaseId: string;
  phaseName: LocalizedText;
  phaseIndex: number;
  durationHint: LocalizedText;
  offsetWeeks: { min: number; max: number };
  hireWhen: LocalizedText;
  /** True when roleId appears in L1 diagramAnchors.roleHints. */
  catalogHint?: boolean;
}

export interface HiringTimelineWave {
  id: HiringWaveId;
  title: LocalizedText;
  items: HiringTimelineItem[];
}

/** Role × delivery-phase hiring sequence (Sprint 27.b) — not ATS. */
export interface HiringTimeline {
  schemaVersion: '1.0.0';
  updatedAt: string;
  phaseCount: number;
  roleCount: number;
  totalHeadcount: number;
  summary: LocalizedText;
  disclaimer: LocalizedText;
  waves: HiringTimelineWave[];
  items: HiringTimelineItem[];
  /** L1 diagramAnchors.roleHints (ROLE_* ids) — catalog honesty strip. */
  catalogRoleHints?: string[];
  catalogRoleCoverage?: {
    covered: string[];
    missing: string[];
  };
}

export type StakeholderLensId = 'cto' | 'pm' | 'security' | 'platform' | 'hr';

export interface StakeholderLensHighlight {
  id: string;
  label: LocalizedText;
  value: LocalizedText;
  sectionRef?: string;
  tone?: 'neutral' | 'warning' | 'positive';
}

export interface StakeholderLens {
  id: StakeholderLensId;
  title: LocalizedText;
  lead: LocalizedText;
  sectionIds: string[];
  highlights: StakeholderLensHighlight[];
}

export interface StakeholderLensesExport {
  schemaVersion: '1.0.0';
  updatedAt: string;
  lenses: StakeholderLens[];
}

export type SprintZeroPriority = 'must' | 'should' | 'could';

export interface SprintZeroStory {
  id: string;
  epicId: string;
  title: LocalizedText;
  acceptanceCriteria: LocalizedText[];
  priority: SprintZeroPriority;
  dependsOn?: string[];
}

export interface SprintZeroEpic {
  id: string;
  phaseId?: string | null;
  title: LocalizedText;
  goal: LocalizedText;
  durationHint?: LocalizedText;
  stories: SprintZeroStory[];
}

export interface SprintZeroBacklog {
  schemaVersion: '1.0.0';
  updatedAt: string;
  mvpEpicId: string | null;
  epicCount: number;
  storyCount: number;
  disclaimer: LocalizedText;
  epics: SprintZeroEpic[];
}

export interface DeliveryPhase {
  id: string;
  name: LocalizedText;
  durationWeeks: LocalizedText;
  outcomes: LocalizedText[];
}

/** Structured WBS story on plan.epics (Next #23 P2) — tracker-ready, not live Jira. */
export interface PlanEpicStory {
  id: string;
  key: string;
  title: LocalizedText;
  priority: SprintZeroPriority;
  acceptanceCriteria: LocalizedText[];
  dependsOn?: string[];
  labels?: string[];
}

export interface PlanEpic {
  id: string;
  key: string;
  title: LocalizedText;
  goal?: LocalizedText;
  phaseId?: string | null;
  labels?: string[];
  stories: PlanEpicStory[];
}

export interface DeliveryPlanPack {
  phases: DeliveryPhase[];
  mvpSlice: LocalizedText;
  summary: LocalizedText;
  requiresUpgrade?: boolean;
  /** Optional structured WBS (plan.epics) when projected for export / Connect. */
  epics?: PlanEpic[];
  epicsSchemaVersion?: string;
}

/** Close CTA on a trust unknown (clarify intake or jump to a Results section). */
export interface RecommendationTrustCloseAction {
  type: 'clarify_intake' | 'jump';
  label: LocalizedText;
  questionId?: string;
  stepId?: 'discovery' | 'requirements' | string;
  href?: string;
  jumpSection?: string;
}

/** Structured trust row — SoT in recommendationTrust.js; arrays stay for schema/export. */
export interface RecommendationTrustItem {
  id: string;
  kind: 'assumption' | 'unknown';
  source: 'intake' | 'topology' | 'path' | 'policy' | 'ai';
  text: LocalizedText;
  close?: RecommendationTrustCloseAction;
}

/** Trust / provenance for the recommendation pack (AI or thin fallback). */
export interface RecommendationTrust {
  source: 'ai' | 'fallback';
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
  whyThisPattern: LocalizedText;
  assumptions: LocalizedText[];
  unknowns: LocalizedText[];
  /** Structured rows with close CTAs (Wave A+); optional for legacy sessions. */
  items?: RecommendationTrustItem[];
  schemaVersion?: string;
}

/** When the premium pack was last aligned to brief + architecture. */
export interface PackBaseline {
  patternId: string;
  architectureFingerprint: string;
  riskCount: number;
  highRiskCount: number;
  effortWeeks?: LocalizedText | null;
  teamHeadcount: number;
  phaseCount: number;
  estimateConfidence?: 'LOW' | 'MEDIUM' | 'HIGH' | null;
}

export interface PackSnapshot {
  inputFingerprint: string;
  generatedAt: string;
  source: 'ai' | 'fallback';
  /** Frozen pack metrics at generation — for what-if diff (Sprint 14). */
  baseline?: PackBaseline;
}

export interface RelatedPatternRef {
  id: string;
  name: LocalizedText;
  domain?: string | null;
  maturity?: string | null;
  architecturalStyle?: string | null;
  /** Wave B fitness reason from scaffold rank (catalog / trust / peak / …). */
  relatedReason?: string | null;
  /** Wave 3 typed relatedLinks kind from L1 (alternative / successor / …). */
  relationKind?: string | null;
}

export interface PatternNarrative {
  description?: LocalizedText | null;
  whenToUse?: LocalizedText | null;
  whenToAvoid?: LocalizedText | null;
  /** L1 catalog rationale — why this shape solves the problem class. */
  rationale?: LocalizedText | null;
  /** Ordered happy-path node ids from L1 (diagram legibility). */
  primaryPath?: string[] | null;
  /** Hybrid decision-language form (omit empty in projection). */
  decisionLanguage?: boolean;
  problem?: LocalizedText | null;
  forces?: LocalizedText[];
  benefits?: LocalizedText[];
  drawbacks?: LocalizedText[];
  issues?: LocalizedText[];
  relatedLinks?: { id: string; kind: string }[];
}

/** Style/name snapshot for compose lint (ADR-010) — filled by scaffold/compose API. */
export interface ComposedPatternMeta {
  id: string;
  architecturalStyle?: string | null;
  name?: LocalizedText;
}

export interface ArchitectureSuggestion {
  patternId: string;
  patternName: LocalizedText;
  rationale: LocalizedText;
  source:
    | 'local_heuristic'
    | 'ai'
    | 'l1_composed'
    | 'l1_composed_validated'
    | 'l1_composed_ai_healed'
    | 'l1_scaffold'
    | 'emergency_stub';
  blueprint: AgnosticBlueprint | null;
  /** Catalog composition hints (Faza 2). */
  relatedPatterns?: RelatedPatternRef[];
  /** Pattern ids included in the current topology (primary[, secondary]). */
  composedFrom?: string[];
  /** Per-pattern style meta for compose review lint (style_conflict). */
  composedMeta?: ComposedPatternMeta[];
  /** Wave 1 post-merge consolidation meta (deterministic, no AI). */
  composeConsolidation?: {
    version?: string;
    mode?: 'validated' | 'sketch';
    resolved?: Array<{
      id: string;
      action: string;
      type: string;
      fromId: string;
      toId: string;
    }>;
    experienceTags?: Array<{ nodeId: string; role: string }>;
  } | null;
  /**
   * L1 catalog prose for Results (description / whenToUse / whenToAvoid).
   * SoT = PATTERN_*.json → agnostic-patterns — not a parallel Cosmos container.
   */
  patternNarrative?: PatternNarrative | null;
  /**
   * After AI diagram refine — deterministic diff vs catalog/composed baseline.
   * Shown in Results brief + board export.
   */
  aiRefineDiff?: import('../utils/aiArchitectureDiff').AiArchitectureDiff | null;
  /**
   * Same-pattern lean prune — ops/platform nodes deferred from this blueprint.
   * Drives match-explain honesty (hide peak complexity “also noted” that no longer apply).
   */
  reducedScopeSlice?: {
    deferredNodeIds: string[];
    deferredTypes: string[];
  } | null;
}

/** Deterministic A/B logical-type diff (Sprint F compare). */
export interface CompareDiff {
  onlyInA: string[];
  onlyInB: string[];
  shared: string[];
  notes: LocalizedText[];
}

export type CompareDeltaBand = 'LOW' | 'MEDIUM' | 'HIGH';
export type CompareDeltaDirection = 'higher' | 'lower' | 'same';

export interface CompareVariantSignal {
  band: CompareDeltaBand;
  label: LocalizedText;
  detail: LocalizedText;
}

export interface CompareDimensionDelta {
  a: CompareVariantSignal;
  b: CompareVariantSignal;
  direction: CompareDeltaDirection;
  note: LocalizedText;
}

/** Directional risk / effort / team signals between compare variants (Sprint 10). */
export interface ComparePackDeltas {
  schemaVersion: string;
  updatedAt: string;
  disclaimer: LocalizedText;
  risk: CompareDimensionDelta;
  effort: CompareDimensionDelta;
  team: CompareDimensionDelta;
  scores: { a: number; b: number };
  /** Scale / NFR context applied to directional deltas (compare intake slice). */
  intakeContextNote?: LocalizedText | null;
}

export interface PackWhatIfDiffLine {
  id: string;
  dimension: 'inputs' | 'architecture' | 'risks' | 'estimate' | 'team' | 'plan';
  direction: 'changed' | 'higher' | 'lower' | 'same' | 'added' | 'removed';
  label: LocalizedText;
  detail: LocalizedText;
  tone: 'neutral' | 'warning' | 'positive';
}

/** Deterministic diff: pack snapshot vs current inputs/topology (B2 what-if). */
export interface PackWhatIfDiff {
  schemaVersion: '1.0.0';
  updatedAt: string;
  changedInputKeys: string[];
  lines: PackWhatIfDiffLine[];
}

/** Side-by-side compare artifact — two L1 scaffolds, no REC_*. */
export interface ComparePack {
  axisId: string;
  /** Hub axis A/B vs post-recommendation lean alternative. */
  kind?: 'axis' | 'lean';
  /** Lean: sibling L1 vs same-pattern topology prune. */
  leanMode?: 'sibling' | 'prune';
  schemaVersion?: string;
  labelA: LocalizedText;
  labelB: LocalizedText;
  patternIdA: string;
  patternIdB: string;
  pickAWhen?: LocalizedText;
  pickBWhen?: LocalizedText;
  /** Lean only — capabilities present in recommended but not lean. */
  giveUpNotes?: LocalizedText[];
  selectionReason?: string | null;
  deferredNodeIds?: string[] | null;
  deferredTypes?: string[] | null;
  /** Lean only — user kept recommended or adopted lean. */
  leanDecision?: 'recommended' | 'lean' | null;
  diff: CompareDiff;
  packDeltas?: ComparePackDeltas | null;
  updatedAt?: string;
}

/** L3 binding accepted by the user (Technologist step). */
export interface TechNodeBinding {
  nodeId: string;
  ref_id: string;
  status: 'accepted' | 'pending';
}

export interface TechMappingPack {
  cloudPreference: 'azure' | 'aws' | 'gcp' | 'oracle' | 'neutral';
  bindings: TechNodeBinding[];
  updatedAt?: string;
}

/** Sprint 29 — LLM/deterministic technologist advisory within TECH_* candidates. */
export interface TechAdvisoryRejected {
  ref_id: string;
  whyNot: LocalizedText;
}

export interface TechAdvisoryNode {
  nodeId: string;
  recommendedRefId: string;
  whyThis: LocalizedText;
  rejected: TechAdvisoryRejected[];
  migrationPath?: LocalizedText | null;
  tradeoffs?: LocalizedText[];
}

export interface TechAdvisoryPack {
  schemaVersion: '1.0.0';
  cloudPreference: 'azure' | 'aws' | 'gcp' | 'oracle' | 'neutral';
  nodes: TechAdvisoryNode[];
  updatedAt?: string;
  source?: 'ai' | 'fallback';
}

/** Directional FinOps from accepted TECH_* + dictionary pricing_guide (no fake $). */
export interface FinOpsFactor {
  name: string;
  impact_level: string;
  description: LocalizedText;
}

export interface FinOpsItem {
  nodeId: string;
  ref_id: string;
  name: LocalizedText;
  pricingModel: string;
  costPredictability: number | null;
  topFactors: FinOpsFactor[];
  savingsTip: LocalizedText | null;
  dictionaryHit?: boolean;
}

/** Structured non-$ cloud cost aggregate (Next #5) — models/factors/scale, not SKU prices. */
export interface FinOpsCloudCostProfile {
  pricingModelCounts: Record<string, number>;
  impactCounts: Record<string, number>;
  avgCostPredictability: number | null;
  dictionaryCoveragePercent: number;
  acceptedCount: number;
  dictionaryHitCount: number;
  scaleRiskBand: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  boardLine: LocalizedText;
  disclaimer: LocalizedText;
}

export type FinOpsDriverId =
  | 'compute'
  | 'data'
  | 'llm'
  | 'integration'
  | 'always_on'
  | 'other';

export type FinOpsBand = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface FinOpsAssumptionDriver {
  id: FinOpsDriverId;
  label: LocalizedText;
  impact: FinOpsBand;
  evidence: LocalizedText;
  factorCount: number;
}

export interface FinOpsAssumptionUnknown {
  id: string;
  wouldChangeBand: boolean;
  text: LocalizedText;
}

export interface FinOpsLaborVsRun {
  estimatePresent: boolean;
  labor: LocalizedText;
  run: LocalizedText;
}

/** Sprint 60 — auditable run-cost drivers + unknowns (no SKU $). */
export interface FinOpsAssumptionBands {
  schemaVersion: '1.0.0';
  band: FinOpsBand;
  primaryDriver: FinOpsDriverId;
  drivers: FinOpsAssumptionDriver[];
  unknowns: FinOpsAssumptionUnknown[];
  laborVsRun: FinOpsLaborVsRun;
  boardLine: LocalizedText;
  disclaimer: LocalizedText;
  /** L1 diagramAnchors.costDrivers — architecture cost themes (no invented $). */
  catalogCostDrivers?: LocalizedText[];
}

/** Azure list-price compare (FINOPS-VENDOR-CALC-PRO Wave 1) — nests under finops. */
export interface CloudCostCompareTotals {
  low: number;
  likely: number;
  high: number;
  currency: string;
  pricedCount?: number;
  lineCount?: number;
}

export interface CloudCostCompareLineItem {
  nodeId: string;
  type?: string;
  ref_id: string | null;
  /** When hostProxy, original OSS / language recommendation. */
  recommendedRefId?: string | null;
  status: 'PRICED' | 'UNPRICED' | 'OSS_SHARED' | 'NO_SKU_MAP' | 'GAP';
  family?: string | null;
  /** OSS runtime priced via managed cloud host SKU. */
  hostProxy?: boolean;
  monthly?: CloudCostCompareTotals | null;
  meter?: {
    id: string;
    name: LocalizedText;
    unit: string;
    unitPriceUsd: number;
    baseQty: number;
    hostProxy?: boolean;
  } | null;
  note?: LocalizedText;
  calculatorUrl?: string;
}

export interface CloudCostComparePriceSource {
  mode?: string;
  liveApi?: boolean;
  vendors?: string[];
  azureLiveLineCount?: number;
  azureFixtureLineCount?: number;
  awsLiveLineCount?: number;
  awsFixtureLineCount?: number;
  gcpLiveLineCount?: number;
  gcpFixtureLineCount?: number;
  oracleLiveLineCount?: number;
  oracleFixtureLineCount?: number;
}

export interface CloudCostCompare {
  schemaVersion: string;
  status: 'PRICED' | 'UNPRICED' | 'EMPTY';
  vendorScope: string;
  priceScope: string;
  stackBasis: string;
  currency: string;
  preferred?: string;
  preferredSource?: string | null;
  honesty?: LocalizedText | null;
  disclaimer?: LocalizedText;
  /** ADR-020 — true while RI/SP/EA / BCM remain parked. */
  commitmentsParked?: boolean;
  commitmentsHonesty?: LocalizedText | null;
  summary: LocalizedText;
  totals?: CloudCostCompareTotals | null;
  resourceClass?: string | null;
  selectedNodeIds?: string[] | null;
  priceSource?: CloudCostComparePriceSource | null;
  columns?: {
    azure?: {
      vendor: string;
      lineItems: CloudCostCompareLineItem[];
      totals?: CloudCostCompareTotals | null;
      calculatorUrl?: string;
      flags?: Record<string, number>;
    };
    aws?: {
      vendor: string;
      lineItems: CloudCostCompareLineItem[];
      totals?: CloudCostCompareTotals | null;
      calculatorUrl?: string;
      flags?: Record<string, number>;
    };
    gcp?: {
      vendor: string;
      lineItems: CloudCostCompareLineItem[];
      totals?: CloudCostCompareTotals | null;
      calculatorUrl?: string;
      flags?: Record<string, number>;
    };
    oracle?: {
      vendor: string;
      lineItems: CloudCostCompareLineItem[];
      totals?: CloudCostCompareTotals | null;
      calculatorUrl?: string;
      flags?: Record<string, number>;
    };
  };
  updatedAt?: string;
}

export interface FinOpsPack {
  status: 'READY' | 'PARTIAL' | 'EMPTY';
  summary: LocalizedText;
  directionalCostSignal: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  /** Board-readable run-cost profile (qualitative). */
  cloudCostProfile?: FinOpsCloudCostProfile | null;
  /** Driver cards + unknowns that could change the band. */
  assumptionBands?: FinOpsAssumptionBands | null;
  /** Wave 1 Azure list-price × assumptions (fixture; Pro via finops redact). */
  cloudCompare?: CloudCostCompare | null;
  items: FinOpsItem[];
  savingsTips: LocalizedText[];
  updatedAt?: string;
}

export type AiCostLineCategory =
  | 'inference'
  | 'embedding'
  | 'vector_db'
  | 'orchestration'
  | 'gpu';

export interface AiCostLineItem {
  id: string;
  category: AiCostLineCategory;
  label: LocalizedText;
  band: LocalizedText;
  driver: LocalizedText;
  sensitivity10x: LocalizedText;
}

export interface AiCostSimulator {
  schemaVersion: '1.0.0';
  updatedAt: string;
  workload: string | null;
  volume: string | null;
  serving: string | null;
  corpus: string | null;
  patternId: string | null;
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
  summary: LocalizedText;
  disclaimer: LocalizedText;
  monthlyBand: LocalizedText;
  sensitivity10x: LocalizedText;
  breakEvenNote: LocalizedText;
  lineItems: AiCostLineItem[];
  assumptions: LocalizedText[];
  unknowns: string[];
}

export type RfpReqCategory =
  | 'functional'
  | 'security'
  | 'compliance'
  | 'scale'
  | 'latency'
  | 'availability'
  | 'integration'
  | 'deliverable'
  | 'constraint'
  | 'other';

export interface RfpExtractedRequirement {
  id: string;
  text: string;
  category: RfpReqCategory;
  line: number;
  reqRef?: string;
}

export interface RfpRequirementsExtraction {
  functional: RfpExtractedRequirement[];
  nonFunctional: RfpExtractedRequirement[];
  constraints: RfpExtractedRequirement[];
  deliverables: RfpExtractedRequirement[];
  lineCount: number;
  requirementCount: number;
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
  locale?: 'pl' | 'en' | 'mixed';
  traceability?: RfpTraceabilityMatrix;
}

export type RfpCoverageStatus = 'covered' | 'partial' | 'gap' | 'unknown' | 'client_specific';

export interface RfpCoverageItem {
  id: string;
  reqRef?: string | null;
  status: RfpCoverageStatus;
  category: RfpReqCategory;
  text: LocalizedText;
  detail: LocalizedText;
}

export interface RfpTraceabilityMatrix {
  matrix: Array<{
    ref: string;
    bucket: string;
    category: string;
    text: string;
    line: number;
  }>;
  byRef: {
    fr: string[];
    nfr: string[];
    ac: string[];
    deliverable: string[];
    other: string[];
  };
  idCount: number;
}

export interface RfpGapAnalysis {
  schemaVersion: '1.0.0' | '1.1.0';
  updatedAt: string;
  briefLength: number;
  requirementCount: number;
  analyzableCount?: number;
  patternId: string | null;
  patternName: LocalizedText | null;
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
  locale?: 'pl' | 'en' | 'mixed';
  procurementProfile?: string;
  summary: LocalizedText;
  disclaimer: LocalizedText;
  extracted: RfpRequirementsExtraction;
  traceability?: RfpTraceabilityMatrix;
  coverage: RfpCoverageItem[];
  gaps: RfpCoverageItem[];
  patternGaps?: RfpCoverageItem[];
  covered: RfpCoverageItem[];
  partial: RfpCoverageItem[];
  unknowns: RfpCoverageItem[];
  clientSpecific?: RfpCoverageItem[];
  packContextUsed: boolean;
  followUps: LocalizedText[];
}

/** Sprint 31 — brownfield HLD review vs nearest L1 pattern. */
export type ArchitectureReviewThemeStatus = 'aligned' | 'partial' | 'gap' | 'unknown';

export interface ArchitectureReviewThemeItem {
  id: string;
  theme: string;
  themeLabel: LocalizedText;
  status: ArchitectureReviewThemeStatus;
  detail: LocalizedText;
}

export interface ArchitectureReviewAlternative {
  patternId: string;
  patternName: LocalizedText;
  score: number;
}

export interface ArchitectureReviewAnalysis {
  schemaVersion: '1.0.0';
  updatedAt: string;
  hldLength: number;
  patternId: string | null;
  patternName: LocalizedText | null;
  matchScore: number;
  trustScore: number;
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
  themeCount?: number;
  summary: LocalizedText;
  disclaimer: LocalizedText;
  aligned: ArchitectureReviewThemeItem[];
  gaps: ArchitectureReviewThemeItem[];
  partial: ArchitectureReviewThemeItem[];
  unknowns: ArchitectureReviewThemeItem[];
  alternatives: ArchitectureReviewAlternative[];
  followUps: LocalizedText[];
}

export type ArchitectureHealthNodeStatus = 'aligned' | 'missing' | 'unverified';

export type ArchitectureHealthBand = 'healthy' | 'drift' | 'misaligned';

export interface ArchitectureHealthNodeItem {
  id: string;
  nodeId: string;
  nodeType: string;
  nodeName: LocalizedText;
  status: ArchitectureHealthNodeStatus;
  detail: LocalizedText;
  matchedSignals: string[];
}

export interface ArchitectureHealthSurprise {
  id: string;
  signalId: string;
  label: string;
  source: string;
  types: string[];
  detail: LocalizedText;
}

export interface ArchitectureHealthAnalysis {
  schemaVersion: '1.0.0';
  updatedAt: string;
  healthScore: number;
  band: ArchitectureHealthBand;
  patternId: string | null;
  nodeCount: number;
  signalCount: number;
  uploadSources: string[];
  summary: LocalizedText;
  disclaimer: LocalizedText;
  aligned: ArchitectureHealthNodeItem[];
  missing: ArchitectureHealthNodeItem[];
  unverified: ArchitectureHealthNodeItem[];
  surprises: ArchitectureHealthSurprise[];
  nfrHints: LocalizedText[];
  followUps: LocalizedText[];
  parseErrors: { source: string; message: string }[];
}

/** Labor board range from catalog market rates × effort weeks (not cloud $). */
export interface BoardEstimateLineItem {
  roleId: string;
  count: number;
  seniority: string;
  hourlyLow: number;
  hourlyHigh: number;
  weeklyLow: number;
  weeklyHigh: number;
}

export interface BoardEstimateAdjustments {
  utilization: number;
  hourlyLow: number | null;
  hourlyHigh: number | null;
  region: 'US' | 'DE' | 'PL' | null;
  hasOverride: boolean;
}

export interface BoardEstimatePack {
  status: string;
  summary: LocalizedText;
  region?: string;
  currency?: string;
  hoursPerWeek?: number;
  weeks?: { min: number; max: number } | null;
  labor?: {
    weeklyLow: number;
    weeklyHigh: number;
    totalLow: number;
    totalHigh: number;
    rangeLabel: LocalizedText;
  } | null;
  lineItems?: BoardEstimateLineItem[];
  /** Wave E4 — client utilization / hourly / region applied to this projection. */
  adjustments?: BoardEstimateAdjustments | null;
  techCoverage?: {
    accepted: number;
    total: number;
    percent: number;
    finopsSignal?: string | null;
  } | null;
  assumptions: LocalizedText[];
  updatedAt: string;
}

/** EstimateEngine Wave E2 — shared directional TCO lines (labor / run / AI / licenses / contingency). */
export type EstimateTcoLineKind =
  | 'labor'
  | 'run'
  | 'ai_workload'
  | 'licenses'
  | 'contingency';

export type EstimateTcoLineStatus = 'READY' | 'QUALITATIVE' | 'DEFERRED' | 'EMPTY';

export interface EstimateTcoLine {
  id: string;
  kind: EstimateTcoLineKind;
  status: EstimateTcoLineStatus;
  label: LocalizedText;
  band: LocalizedText | null;
  amountLow: number | null;
  amountHigh: number | null;
  currency: string | null;
  assumptions: LocalizedText[];
  sourceArtifact: string | null;
}

export interface EstimateTcoDriver {
  id: string;
  kind: string;
  note: LocalizedText;
}

export interface EstimateEffortOmp {
  optimistic: number;
  mostLikely: number;
  pessimistic: number;
  contingencyPct: number;
  label: LocalizedText;
}

export type PlanEstimateReconcileStatus = 'ALIGNED' | 'LOOSE' | 'DIVERGED' | 'INCOMPLETE';

export interface PlanEstimateReconcile {
  schemaVersion: '1.0.0';
  status: PlanEstimateReconcileStatus;
  estimateWeeks: { min: number; max: number } | null;
  planWeeks: {
    midSum: number;
    minSum: number;
    maxSum: number;
    phaseCount: number;
  } | null;
  gapRatio: number | null;
  summary: LocalizedText;
}

/** Wave E6 — MVP cut vs full first-release capability scope. */
export interface EstimateScopeDeltaScenario {
  id: 'mvp' | 'full';
  label: LocalizedText;
  capabilityIds: string[];
  capabilityLabels: LocalizedText;
  capabilityCount: number;
  effortWeeks: LocalizedText;
  weeks: { min: number; max: number; mid: number };
  bumpWeeks: number;
}

export interface EstimateScopeDeltaLaborSide {
  low: number;
  high: number;
  band: LocalizedText;
}

export interface EstimateScopeDelta {
  schemaVersion: '1.0.0';
  status: 'READY' | 'EMPTY';
  mvpMaxCapabilities: number;
  headline: LocalizedText;
  summary: LocalizedText;
  scenarios: {
    mvp: EstimateScopeDeltaScenario;
    full: EstimateScopeDeltaScenario;
  } | null;
  laborDelta: {
    status: 'READY' | 'DEFERRED';
    currency: string | null;
    full: EstimateScopeDeltaLaborSide | null;
    mvp: EstimateScopeDeltaLaborSide | null;
    saved: EstimateScopeDeltaLaborSide | null;
    note: LocalizedText;
  } | null;
  runDelta: {
    status: 'QUALITATIVE' | 'EMPTY';
    band: LocalizedText;
    note: LocalizedText;
  } | null;
  weeksSaved: {
    min: number;
    max: number;
    mid: number;
    label: LocalizedText;
  } | null;
  deferredCapabilities?: Array<{ id: string; label: LocalizedText }>;
  disclaimer: LocalizedText;
}

export interface EstimateTcoPack {
  schemaVersion: '1.0.0' | '1.1.0' | '1.2.0' | '1.3.0';
  accuracyClass: 'ROM';
  status: 'READY' | 'PARTIAL' | 'EMPTY';
  summary: LocalizedText;
  lines: EstimateTcoLine[];
  drivers: EstimateTcoDriver[];
  /** Wave E3 — directional reserve % applied to pessimistic weeks. */
  contingencyPct?: number | null;
  /** Wave E3 — optimistic / most-likely / pessimistic effort weeks. */
  omp?: EstimateEffortOmp | null;
  /** Wave E3 — plan phase weeks vs estimate effort band. */
  planReconcile?: PlanEstimateReconcile | null;
  /** Wave E6 — MVP vs full capability_scope delta. */
  scopeDelta?: EstimateScopeDelta | null;
  disclaimer: LocalizedText;
  updatedAt: string;
}

/** Board-ready ADR-style record (Sprint 1 post-PROD — deterministic, not LLM). */
export type DecisionLogCategory =
  | 'architecture'
  | 'requirements'
  | 'technology'
  | 'estimate'
  | 'finops'
  | 'integration'
  | 'build_vs_buy'
  | 'migration'
  | 'compare'
  | 'delivery'
  | 'workshop';

export type DecisionScoreAxis = 'cost' | 'risk' | 'speed' | 'ops';

/** 1–5, higher = more favorable for the recommendation (Sprint 38). */
export interface DecisionAxisScores {
  cost: number;
  risk: number;
  speed: number;
  ops: number;
}

export interface DecisionCardScoring {
  schemaVersion: '1.0.0';
  axes: DecisionAxisScores;
  winnerAxes: DecisionScoreAxis[];
  tradeStudy: LocalizedText;
}

export interface DecisionScoringHighlight {
  cardId: string;
  title: LocalizedText;
  winnerAxes: DecisionScoreAxis[];
  tradeStudy: LocalizedText;
}

export interface DecisionScoringRollup {
  schemaVersion: '1.0.0';
  scoredCardCount: number;
  winnerAxisCounts: Record<DecisionScoreAxis, number>;
  summary: LocalizedText;
  highlights: DecisionScoringHighlight[];
}

export interface DecisionLogEntry {
  id: string;
  category: DecisionLogCategory;
  source?: Record<string, unknown>;
  title: LocalizedText;
  context: LocalizedText;
  decision: LocalizedText;
  consequences: LocalizedText[];
  scoring?: DecisionCardScoring | null;
  /** ISO timestamp when the user applied a gap-close (or similar) decision. */
  appliedAt?: string;
}

export interface DecisionLog {
  schemaVersion: '1.0.0' | '1.1.0' | '1.2.0';
  updatedAt: string;
  entryCount: number;
  entries: DecisionLogEntry[];
  summary: LocalizedText;
  scoringRollup?: DecisionScoringRollup | null;
  workshopAttribution?: {
    schemaVersion: string;
    contributorCount: number;
    contributors: Array<{
      id?: string;
      displayName: string;
      role: string;
      roleLabel?: LocalizedText;
      submittedAt?: string | null;
      addedLineEn?: string;
      addedLinePl?: string;
      note?: string;
    }>;
    summary: LocalizedText;
    disclaimer: LocalizedText;
  } | null;
}

/** MADR-shaped ADR record from decision log (Sprint 39 — user-owned, not SoT). */
export type AdrStatus = 'accepted';

export interface AdrAlternative {
  id: string;
  title: LocalizedText;
  detail: LocalizedText;
}

export interface AdrRecord {
  id: string;
  decisionLogEntryId: string;
  category: DecisionLogCategory;
  status: AdrStatus;
  title: LocalizedText;
  context: LocalizedText;
  decision: LocalizedText;
  consequences: LocalizedText[];
  alternatives: AdrAlternative[];
  scoring?: DecisionCardScoring | null;
  source?: Record<string, unknown> | null;
}

export interface AdrWorkflow {
  schemaVersion: '1.0.0';
  outline: 'madr';
  updatedAt: string;
  recordCount: number;
  decisionLogSchemaVersion?: string | null;
  records: AdrRecord[];
  summary: LocalizedText;
  disclaimer: LocalizedText;
}

/** Agentic AI governance checklist (Sprint 32 / handoff 40). */
export type AgenticGovernanceStatus = 'pass' | 'partial' | 'gap' | 'unknown';

export type AgenticGovernanceBand =
  | 'not_ready'
  | 'foundational'
  | 'production_ready'
  | 'governed';

export type AgenticGovernanceDimension =
  | 'governance_tier'
  | 'memory_policy'
  | 'agent_firewall'
  | 'cost_attribution'
  | 'human_oversight'
  | 'audit_trail';

export interface AgenticGovernanceItem {
  id: string;
  dimension: AgenticGovernanceDimension;
  status: AgenticGovernanceStatus;
  title: LocalizedText;
  detail: LocalizedText;
  action: LocalizedText;
  gapNarrative?: LocalizedText | null;
  relatedNodeIds?: string[];
  intakeKey?: string | null;
  intakeValue?: string | null;
}

export interface AgenticGovernanceGapNarrative {
  itemId: string;
  dimension: AgenticGovernanceDimension;
  narrative: LocalizedText;
}

export interface AgenticGovernance {
  schemaVersion: '1.0.0';
  updatedAt: string;
  patternId: string | null;
  readinessScore: number;
  band: AgenticGovernanceBand;
  intake: {
    agent_gov_tier: string | null;
    agent_memory: string | null;
    agent_firewall: string | null;
    agent_cost_attr: string | null;
  };
  items: AgenticGovernanceItem[];
  gapCount: number;
  partialCount: number;
  passCount: number;
  unknowns: string[];
  gapNarratives: AgenticGovernanceGapNarrative[];
  summary: LocalizedText;
  disclaimer: LocalizedText;
}

/** Compliance gap analysis (Sprint 33 / handoff 41). */
export type ComplianceGapStatus = 'pass' | 'partial' | 'gap' | 'unknown';

export type ComplianceGapBand =
  | 'not_ready'
  | 'foundational'
  | 'substantial'
  | 'board_ready';

export type ComplianceLens = 'gdpr' | 'soc2' | 'eu_ai_act';

export interface ComplianceGapItem {
  id: string;
  lens: ComplianceLens;
  control: string;
  status: ComplianceGapStatus;
  title: LocalizedText;
  detail: LocalizedText;
  action: LocalizedText;
  gapNarrative?: LocalizedText | null;
  relatedNodeIds?: string[];
}

export interface ComplianceGapNarrative {
  itemId: string;
  lens: ComplianceLens;
  control: string;
  narrative: LocalizedText;
}

export interface ComplianceGaps {
  schemaVersion: '1.0.0';
  updatedAt: string;
  patternId: string | null;
  readinessScore: number;
  band: ComplianceGapBand;
  lenses: ComplianceLens[];
  sources: string[];
  flags: string[];
  orgPreferences: {
    complianceFlags: string[];
  };
  intake: {
    constraints: string | null;
    agent_gov_tier: string | null;
  };
  items: ComplianceGapItem[];
  gapCount: number;
  partialCount: number;
  passCount: number;
  gapNarratives: ComplianceGapNarrative[];
  summary: LocalizedText;
  disclaimer: LocalizedText;
}

export type ExecutiveSummaryTone = 'neutral' | 'warning' | 'positive';

export interface ExecutiveSummaryBullet {
  id: string;
  label: LocalizedText;
  value: LocalizedText;
  tone?: ExecutiveSummaryTone;
}

/** Board one-pager — deterministic snapshot (Sprint 9). */
export interface ExecutiveSummary {
  schemaVersion: '1.0.0';
  updatedAt: string;
  headline: LocalizedText;
  subhead: LocalizedText;
  bullets: ExecutiveSummaryBullet[];
  boardPackComplete: boolean;
  missingBoardSections: string[];
}

export type BriefingNextActionKind = 'upgrade' | 'rebuild_pack' | 'none';

export interface ResultsBriefingSnapshot {
  patternName: LocalizedText;
  confidence: string | null;
  boardComplete: boolean;
  missingBoardSections: string[];
  presentBoardSections: string[];
}

export interface ResultsBriefingNextAction {
  kind: BriefingNextActionKind;
  label: LocalizedText;
}

/** Problem → architecture → alternatives → implications (Wave C). */
export interface ResultsBriefingCausalChain {
  problem: {
    headline: LocalizedText;
    detail: LocalizedText;
  };
  architecture: {
    patternName: LocalizedText;
    why: LocalizedText;
  };
  /** Primary intake answers that moved the main diagram (answer → effect trail). */
  drivers?: Array<{ id: string; questionId: string; label: LocalizedText }>;
  alternatives: Array<{ id: string; effect: LocalizedText }>;
  implications: Array<{ id: string; label: LocalizedText; value: LocalizedText }>;
}

/** One professional briefing — situation + snapshot + insights (replaces banner + exec summary). */
export interface ResultsBriefing {
  schemaVersion: '1.0.0' | '1.1.0';
  updatedAt: string;
  situation: {
    headline: LocalizedText;
    detail: LocalizedText;
  };
  snapshot: ResultsBriefingSnapshot;
  /** Causal narrative — problem → solution → related → pack implications. */
  causalChain?: ResultsBriefingCausalChain;
  insights: ExecutiveSummaryBullet[];
  nextAction: ResultsBriefingNextAction;
  packFallback: boolean;
}

export type GapCloseSource =
  | 'integration_risk'
  | 'board_missing'
  | 'pack_risk'
  | 'catalog_risk'
  | 'compliance'
  | 'trust_unknown'
  | 'threat_control';

export type GapCloseActionType =
  | 'accept_decision_card'
  | 'compose_related_pattern'
  | 'rebuild_pack'
  | 'jump'
  | 'show_proposal'
  | 'propose_ai'
  | 'clarify_intake'
  | 'apply_overlay'
  | 'record_decision';

export interface GapCloseAction {
  type: GapCloseActionType;
  label: LocalizedText;
  cardId?: string | null;
  patternId?: string | null;
  href?: string | null;
  /** Intake question to reopen (clarify_intake). */
  questionId?: string | null;
  /** Wizard step for clarify_intake (discovery | requirements). */
  stepId?: 'discovery' | 'requirements' | null;
  /** Force-add resolve overlay onto the engagement blueprint (security / HA / firewall). */
  overlayId?: 'identity_provider' | 'secrets_manager' | 'ha_progressive_delivery' | 'firewall_policy' | string | null;
}

export interface GapCloseFinding {
  id: string;
  source: GapCloseSource;
  severity: string;
  diagnosis: LocalizedText;
  targetState: LocalizedText;
  /** Concrete mitigation / how-to-reduce used as offline proposal steps. */
  howToReduce?: LocalizedText | null;
  doneWhen?: LocalizedText | null;
  steps?: LocalizedText[];
  diagramChanges?: LocalizedText[];
  actions: GapCloseAction[];
  mitigationCardId?: string | null;
}

export interface GapCloseAdvice {
  schemaVersion: string;
  updatedAt: string;
  findings: GapCloseFinding[];
}

export interface GapCloseProposalApply {
  type: Exclude<GapCloseActionType, 'propose_ai' | 'jump' | 'clarify_intake' | 'show_proposal'> | 'none';
  cardId?: string | null;
  patternId?: string | null;
  href?: string | null;
  overlayId?: string | null;
  label: LocalizedText;
}

export interface GapCloseProposal {
  schemaVersion: string;
  source: 'deterministic' | 'ai';
  findingId: string;
  findingSource?: GapCloseSource;
  title: LocalizedText;
  diagnosis: LocalizedText;
  targetState: LocalizedText;
  /** Concrete close steps (problem → solution). */
  steps: LocalizedText[];
  /** Outcome check — distinct from a single mitigation step when possible. */
  doneWhen: LocalizedText;
  /** What should appear / change on the architecture diagram. */
  diagramChanges: LocalizedText[];
  apply: GapCloseProposalApply;
  questions: LocalizedText[];
  disclaimer: LocalizedText;
}

/** Secondary architecture signals pack (Sprint 21 / B3). */
export type MultiSignalTone = 'sky' | 'amber' | 'violet';

export interface MultiSignal {
  id: string;
  tone: MultiSignalTone;
  headline: LocalizedText;
  detail: LocalizedText;
  relatedPatternIds?: string[];
}

export interface MultiSignalPack {
  schemaVersion: '1.0.0';
  updatedAt: string;
  primaryPatternId: string;
  signalCount: number;
  signals: MultiSignal[];
  summary: LocalizedText;
}

export type IntegrationDecisionTopic =
  | 'protocol'
  | 'sync_async'
  | 'messaging'
  | 'resiliency'
  | 'transaction';

export interface IntegrationDecisionEdgeRef {
  sourceId: string;
  targetId: string;
}

export interface IntegrationDecisionCard {
  id: string;
  topic: IntegrationDecisionTopic;
  title: LocalizedText;
  context: LocalizedText;
  recommendation: LocalizedText;
  tradeoffs: LocalizedText[];
  edgeLabels?: LocalizedText[];
  /** Structured edge refs for linked diagram reading (Results UX). */
  linkedEdges?: IntegrationDecisionEdgeRef[];
  /** Node ids when the decision is about a node type (cache, gateway, …). */
  linkedNodeIds?: string[];
  /** Sibling cards when soft-dedupe links related decisions (same async edge set). */
  relatedCardIds?: string[];
  scoring?: DecisionCardScoring;
}

export interface IntegrationDecisionCards {
  schemaVersion: '1.0.0' | '1.1.0' | string;
  updatedAt: string;
  cardCount: number;
  cards: IntegrationDecisionCard[];
  summary: LocalizedText;
}

/** Sprint 59 — integration architecture panel (deterministic sketch). */
export type IntegrationSystemRole = 'source' | 'target' | 'both' | 'landscape';
export type SyncAsyncEmphasis = 'sync' | 'async' | 'mixed' | 'unknown';
export type IntegrationRiskSeverity = 'high' | 'medium' | 'low';

export interface IntegrationArchitectureStyle {
  id: string;
  label: LocalizedText;
}

export interface IntegrationArchitectureSystem {
  id: string;
  role: IntegrationSystemRole;
  label: LocalizedText;
  flag?: string;
  nodeType?: string | null;
}

export interface IntegrationSyncAsync {
  emphasis: SyncAsyncEmphasis;
  syncCount: number;
  asyncCount: number;
  narrative: LocalizedText;
}

/** How the linked decision card relates to an integration risk. */
export type IntegrationMitigationRole = 'closes' | 'supports';

export interface IntegrationArchitectureRisk {
  id: string;
  severity: IntegrationRiskSeverity;
  title: LocalizedText;
  detail: LocalizedText;
  mitigationCardId?: string;
  /**
   * 'closes' = card addresses a real sketch gap (e.g. missing hub).
   * 'supports' = related hygiene under an existing hub (bypass watch) — not “closes this gap”.
   */
  mitigationRole?: IntegrationMitigationRole;
  mitigation: LocalizedText;
  /** How the estate should look — specific, not a generic “add a gateway” when a hub exists. */
  targetState?: LocalizedText;
  /** When true, gap-close leads with compose of a hub/gateway pattern. */
  composeRecommended?: boolean;
}

export interface IntegrationEndpointBrief {
  id: string;
  role: 'source' | 'target';
  systemClass: string;
  label: LocalizedText;
  vendorHint?: string | null;
  locked: boolean;
  lockedTechId?: string | null;
  lockedTechLabel?: LocalizedText | null;
  processDomain?: string | null;
  processDomainLabel?: LocalizedText | null;
  processChange?: string | null;
  processChangeLabel?: LocalizedText | null;
  store: 'yes' | 'no' | 'optional';
  storeLabel: LocalizedText;
  exchange: 'sync' | 'async' | 'mixed';
  exchangeLabel: LocalizedText;
  resiliency: LocalizedText;
}

export interface IntegrationArchitecture {
  schemaVersion: string;
  updatedAt: string;
  styles: IntegrationArchitectureStyle[];
  systems: IntegrationArchitectureSystem[];
  systemsByRole?: Record<IntegrationSystemRole, IntegrationArchitectureSystem[]>;
  syncAsync: IntegrationSyncAsync;
  risks: IntegrationArchitectureRisk[];
  relatedCardIds: string[];
  cardCount: number;
  edgeCount: number;
  landscapeFlags: string[];
  /** Locked source/target design briefs. Empty when the journey did not lock systems. */
  endpoints?: IntegrationEndpointBrief[];
  /** Overlay-applied blueprint used by the diagram (intake landscape + capabilities). */
  diagramBlueprint?: AgnosticBlueprint | null;
  summary: LocalizedText;
  disclaimer: LocalizedText;
}

/** Sprint 59 — threat & control sketch (advisory, not certification). */
export type ThreatConfidence = 'high' | 'medium' | 'low';
export type ThreatAssetKind = 'identity' | 'data' | 'edge' | 'secrets' | 'ai' | 'topology';
export type ThreatControlSource = 'blueprint' | 'nfr' | 'compliance' | 'pattern' | 'gap';
export type ThreatControlStatus = 'implied' | 'partial' | 'unknown';

export interface ThreatModelAsset {
  id: string;
  kind: ThreatAssetKind;
  label: LocalizedText;
  relatedNodeIds: string[];
}

export interface ThreatModelThreat {
  id: string;
  assetId: string;
  title: LocalizedText;
  detail: LocalizedText;
  confidence: ThreatConfidence;
  /** Blueprint nodes for linked diagram reading (from asset.relatedNodeIds). */
  linkedNodeIds?: string[];
}

export interface ThreatModelControl {
  id: string;
  threatId: string | null;
  title: LocalizedText;
  detail: LocalizedText;
  source: ThreatControlSource;
  status: ThreatControlStatus;
  /** Blueprint nodes for linked diagram reading (inherited from threat asset). */
  linkedNodeIds?: string[];
  /** Next-step CTAs when status is unknown/partial (gap-close action shape). */
  actions?: GapCloseAction[];
}

export interface ThreatModelUnknown {
  id: string;
  detail: LocalizedText;
  actions?: GapCloseAction[];
}

export interface ThreatModelLite {
  schemaVersion: string;
  updatedAt: string;
  patternId: string | null;
  confidence: ThreatConfidence;
  assets: ThreatModelAsset[];
  threats: ThreatModelThreat[];
  controls: ThreatModelControl[];
  /** Structured unknowns (legacy LocalizedText still accepted by projectors). */
  unknowns: Array<ThreatModelUnknown | LocalizedText>;
  flags: string[];
  summary: LocalizedText;
  disclaimer: LocalizedText;
}

export type BuildVsBuyStance = 'build' | 'buy' | 'extend';

export interface BuildVsBuyCard {
  id: string;
  nodeId: string;
  nodeType: string;
  stance: BuildVsBuyStance;
  title: LocalizedText;
  context: LocalizedText;
  recommendation: LocalizedText;
  tradeoffs: LocalizedText[];
  impactHints: LocalizedText[];
  scoring?: DecisionCardScoring;
  /** Blueprint node ids for linked diagram reading (usually [nodeId]). */
  linkedNodeIds?: string[];
  linkedEdges?: IntegrationDecisionEdgeRef[];
}

export interface BuildVsBuyCards {
  schemaVersion: '1.0.0' | '1.1.0' | string;
  updatedAt: string;
  cardCount: number;
  cards: BuildVsBuyCard[];
  mixSummary: LocalizedText;
  summary: LocalizedText;
}

/** Sprint 59 Wave B — data ownership / PII / retention cards on DATA_* nodes. */
export type DataStoreClassification =
  | 'system_of_record'
  | 'analytics'
  | 'derived'
  | 'ephemeral'
  | 'corpus';

export type DataPiiLevel = 'high' | 'medium' | 'low';

export interface DataOwnershipCard {
  id: string;
  nodeId: string;
  /** Blueprint node ids for linked diagram reading (usually [nodeId]). */
  linkedNodeIds?: string[];
  nodeType: string;
  classification: DataStoreClassification;
  classificationLabel: LocalizedText;
  piiLevel: DataPiiLevel;
  title: LocalizedText;
  context: LocalizedText;
  ownership: LocalizedText;
  pii: LocalizedText;
  retention: LocalizedText;
  recommendation: LocalizedText;
  tradeoffs: LocalizedText[];
}

export interface DataOwnershipCards {
  schemaVersion: string;
  updatedAt: string;
  cardCount: number;
  cards: DataOwnershipCard[];
  summary: LocalizedText;
  disclaimer: LocalizedText;
}

/** ADR-015 Wave C — architecture plane cards from diagram joins. */
export type ArchitectureLayerKind =
  | 'experience'
  | 'gateway'
  | 'connectivity'
  | 'application'
  | 'integration'
  | 'ai'
  | 'data'
  | 'identity'
  | 'controls'
  | 'observability';

export type ArchitectureLayerIdentityClass = 'workforce' | 'ciam' | 'default';

export interface ArchitectureLayerTechPick {
  ref_id: string;
  /** Blueprint node that owns this pick — used to open Node Playbook from plane cards. */
  nodeId?: string | null;
  name: LocalizedText | string;
  whyHere: LocalizedText;
  recommended?: boolean;
}

export interface ArchitectureLayerCardNode {
  id: string;
  type: string | null;
  layer: string | null;
  name: LocalizedText | string;
}

export interface ArchitectureLayerDiagramJoin {
  /** Mermaid band keys present on this plane (e.g. Security Tier). */
  layers: string[];
  /** Bilingual band · node body (UI/export add the “On the diagram” label). */
  line: LocalizedText;
}

export interface ArchitectureLayerCard {
  id: string;
  kind: ArchitectureLayerKind;
  title: LocalizedText;
  why: LocalizedText;
  whenNot: LocalizedText;
  relatedAlternatives: LocalizedText;
  linkedNodeIds: string[];
  nodeCount: number;
  nodes: ArchitectureLayerCardNode[];
  /** Join back to diagram bands so Security≠Identity is readable. */
  diagramJoin?: ArchitectureLayerDiagramJoin;
  /** Present on identity cards when lead TECH implies workforce vs CIAM copy. */
  identityClass?: ArchitectureLayerIdentityClass;
  techShortlist: ArchitectureLayerTechPick[];
}

export interface ArchitectureLayerCards {
  schemaVersion: string;
  updatedAt: string;
  cardCount: number;
  cards: ArchitectureLayerCard[];
  summary: LocalizedText;
  /** Explains diagram bands vs decision planes. */
  legend?: LocalizedText;
  disclaimer: LocalizedText;
}

/** Trust gate — when estimate/export should stay advisory (Sprint 19 B5; UX 1.1.0). */
export type TrustGateSeverity = 'high' | 'medium';

export type TrustGateJumpSection = 'requirements' | 'estimate' | 'team' | 'plan';

export interface TrustGateBlocker {
  id: string;
  severity: TrustGateSeverity;
  label: LocalizedText;
  detail: LocalizedText;
  /** Consequence for estimate / export (not architecture validity). */
  impact?: LocalizedText;
  actionLabel?: LocalizedText;
  jumpSection?: TrustGateJumpSection | string | null;
  /** Named gaps (humanized unknowns or missing pack sections). */
  items?: LocalizedText[];
}

export interface TrustGate {
  schemaVersion: '1.0.0' | '1.1.0';
  updatedAt: string;
  gated: boolean;
  boardPackComplete: boolean;
  missingBoardSections: string[];
  unknownCount: number;
  confidence: 'LOW' | 'MEDIUM' | 'HIGH' | null;
  blockers: TrustGateBlocker[];
  headline: LocalizedText;
  summary: LocalizedText;
}

/** Post-merge compose lint (ADR-010 Sprint 24). */
export type ComposeReviewSeverity = 'info' | 'warning' | 'high';

export interface ComposeReviewFinding {
  id: string;
  severity: ComposeReviewSeverity;
  label: LocalizedText;
  detail: LocalizedText;
}

export interface ComposeReviewLint {
  schemaVersion: '1.0.0' | '1.1.0' | '1.2.0';
  updatedAt: string;
  reviewRecommended: boolean;
  /** Post-consolidation mode (Wave 1). */
  composeMode?: 'validated' | 'sketch';
  /** High residual conflicts — further Add blocked (Wave 2). */
  blocking?: boolean;
  composedFrom: string[];
  patternCount: number;
  findingCount: number;
  findings: ComposeReviewFinding[];
  summary: LocalizedText;
  /** True when further Add-to-diagram is blocked. */
  softStopped?: boolean;
}

/** Enterprise org prefs reflected on Results (B4). */
export interface OrgPreferenceChip {
  id: string;
  label: LocalizedText;
  active: boolean;
}

export interface OrgPreferencesApplied {
  schemaVersion: '1.0.0';
  updatedAt: string;
  preferredCloud: string;
  complianceFlags: string[];
  teamModel: string | null;
  activeConstraintFlags: string[];
  chips: OrgPreferenceChip[];
  anyActive: boolean;
  summary: LocalizedText;
}

export type AgenticArtifactKind =
  | 'requirements'
  | 'architecture'
  | 'compare'
  | 'risks'
  | 'estimate'
  | 'team'
  | 'plan'
  | 'techMapping'
  | 'finops'
  | 'trust';

export type ArtifactFeedbackRating = 'up' | 'down';

export interface ArtifactFeedbackEntry {
  rating: ArtifactFeedbackRating;
  reason?: string;
  updatedAt: string;
}

export type ArtifactFeedbackMap = Partial<Record<AgenticArtifactKind, ArtifactFeedbackEntry>>;

export interface AgenticArtifacts {
  /**
   * Canonical Architecture Brief — validated intake gate (ADR-016).
   * Built from guided answers / template / RFP mapper before recommendation.
   */
  canonicalBrief?: import('../config/canonicalArchitectureBrief').CanonicalArchitectureBrief | null;
  /** Last CAB gate result snapshot (HARD/SOFT/PASS + errors). Not board-facing. */
  canonicalBriefGate?: {
    ok: boolean;
    gate: string;
    errors: Array<{ code: string; section: string; message: string; level: string }>;
    reason?: string;
  } | null;
  requirements: RequirementsPack | null;
  architecture: ArchitectureSuggestion | null;
  /** Sprint F — second variant for compare intent */
  architectureAlt?: ArchitectureSuggestion | null;
  /** Sprint F — deterministic A/B diff + axis metadata */
  compare?: ComparePack | null;
  risks: RiskRegister | null;
  estimate: EstimatePack | null;
  team: TeamPack | null;
  plan: DeliveryPlanPack | null;
  trust: RecommendationTrust | null;
  techMapping: TechMappingPack | null;
  techAdvisory: TechAdvisoryPack | null;
  architectureReview: ArchitectureReviewAnalysis | null;
  architectureHealth: ArchitectureHealthAnalysis | null;
  finops: FinOpsPack | null;
  /** Labor $ from ROLE rates × weeks — directional board number. */
  boardEstimate: BoardEstimatePack | null;
  /** Shared TCO lines (EstimateEngine E2) — labor / run / AI / licenses / contingency. */
  estimateTco?: EstimateTcoPack | null;
  /** Inputs at last pack generation — stale detection (ADR-008 slice 7c). */
  packSnapshot?: PackSnapshot;
  /** Thumbs feedback — learning signal only; never auto-writes SoT. */
  feedback: ArtifactFeedbackMap;
  /**
   * Gap-close Apply decisions persisted on the engagement (Sprint 60 Wave B).
   * Merged into the decision log + export; cleared when architecture is invalidated.
   */
  gapCloseApplied?: DecisionLogEntry[];
  /**
   * Bilateral P1.4 — ADR markdown imported from Git (confirm gate).
   * Merged into buildAdrWorkflow ahead of generated decision-log records.
   */
  importedAdrRecords?: AdrRecord[];
  /**
   * Next #23 P0 — Platform Profile (locked estate for Change-on-platform).
   * Engagement-scoped; never LeanIX / L1 dual SoT.
   */
  platformProfile?: import('../config/platformProfile').PlatformProfile | null;
  /** Next #23 P3 — accepted estate HLD snapshot for design-steer. */
  acceptedHld?: {
    schemaVersion?: string;
    kind?: string;
    profileId?: string | null;
    presetId?: string | null;
    systems?: Array<{ id: string; role: string; systemClass?: string; label?: LocalizedText }>;
    lockedNodeIds?: string[];
    presentSystemIds?: string[];
    citesGatewayBroker?: boolean;
    acceptedAt?: string | null;
    updatedAt?: string | null;
  } | null;
}

export interface AgenticEngagementState {
  schemaVersion: typeof AGENTIC_ENGAGEMENT_SCHEMA_VERSION;
  /**
   * Question-bank version (intake). Separate from engagement schemaVersion.
   * @see docs/agnostic-architecture/INTAKE_SCHEMA.md
   */
  intakeSchemaVersion?: string;
  currentStepId: AgenticStepId;
  answers: Record<string, string>;
  notes: Record<string, string>;
  completedSteps: AgenticStepId[];
  artifacts: AgenticArtifacts;
  /** Optional assist chat (drawer), not the primary UX. */
  assistOpen: boolean;
  /** Hub entry — Sprint B stores; Sprint C filters progress/gates. */
  mode?: 'full' | 'task';
  intent?: string | null;
  /**
   * Locked / preferred L1 from an engagement start-seed (e.g. pattern catalog).
   * When set with patternLockSource, architecture ignores resolver primary.
   */
  preferredPatternId?: string | null;
  /**
   * Who seeded the lock — `catalog` shortens intake; `related_switch` keeps an explicit
   * Use-instead pick from being wiped by answer↔architecture auto-rebuild.
   */
  patternLockSource?: 'catalog' | 'related_switch' | null;
  /**
   * Snapshot of compare Results before “Continue with A/B”.
   * Back restores this instead of single-architecture edit.
   */
  compareCheckpoint?: CompareCheckpoint | null;
  /** One-shot: intake bank bumped under this draft — show soft note. */
  intakeCompatNote?: boolean;
  /** User expanded task Results to full board layout (Expand CTA). */
  expandedFromTask?: boolean;
  /** Task intent id before Expand — progress label / step-help chrome only. */
  expandedFromIntent?: string | null;
  /** Sprint 35 — server-owned workshop shares + attributed contributions. */
  workshop?: {
    schemaVersion?: string;
    shares?: Array<Record<string, unknown>>;
    contributions?: Array<Record<string, unknown>>;
  } | null;
}

/** Frozen compare view for back-navigation after picking a variant. */
export interface CompareCheckpoint {
  answers: Record<string, string>;
  notes: Record<string, string>;
  architecture: ArchitectureSuggestion | null;
  architectureAlt: ArchitectureSuggestion | null;
  compare: ComparePack | null;
  completedSteps: AgenticStepId[];
}

export const AGENTIC_STEP_ORDER: AgenticStepId[] = [
  'discovery',
  'requirements',
  'architecture',
  'risks',
  'estimate',
  'team',
  'plan',
  'summary',
];

/**
 * Steps shown in the progress bar (matches real user journey).
 * Risks / estimate / team / plan live inside the results screen, not as separate wizard pages.
 */
export const AGENTIC_PROGRESS_STEPS: AgenticStepId[] = [
  'discovery',
  'requirements',
  'architecture',
  'summary',
];

/** Map any internal step onto the progress milestone the user sees. */
export function toProgressStepId(stepId: AgenticStepId): AgenticStepId {
  if (stepId === 'risks' || stepId === 'estimate' || stepId === 'team' || stepId === 'plan') {
    return 'summary';
  }
  return stepId;
}
